import { useCallback } from 'react';

import type {
  ControlMessageCategory,
  UseCategoryFormMutationsArgs,
  UseCategoryMutationsArgs,
} from '@/types/category';

import { ADD, ALL, CLOSED, EDIT } from '@/constants';
import { useCreateCategoryMutation } from '@/hooks/api/use-category-mutations/create';
import { useDeleteCategoryMutation } from '@/hooks/api/use-category-mutations/delete';
import { useEditCategoryMutation } from '@/hooks/api/use-category-mutations/edit';
import { parseEditingCategoryId } from '@/lib/helpers/categories';

export function useCategoryFormMutations({
  mode,
  predefinedMessageCategoryId,
  onSuccess,
}: UseCategoryFormMutationsArgs) {
  const create = useCreateCategoryMutation();
  const edit = useEditCategoryMutation();

  const onValidSubmit = useCallback(
    async (values?: { name?: string }) => {
      const name = values?.name?.trim() ?? '';

      try {
        if (mode === ADD) {
          await create.mutateAsync({ name });
        } else {
          if (predefinedMessageCategoryId === undefined) {
            return;
          }

          await edit.mutateAsync({ id: predefinedMessageCategoryId, name });
        }

        onSuccess();
      } catch (error) {
        console.error('Failed to save category:', error);
      }
    },
    [predefinedMessageCategoryId, create, edit, mode, onSuccess]
  );

  const isSaving = create.isPending || edit.isPending;

  return { onValidSubmit, isSaving };
}

export function useCategoryMutations({
  selectedCategoryId,
  onSelectCategoryId,
  setCategoryForm,
}: UseCategoryMutationsArgs) {
  const deleteCategoryMutation = useDeleteCategoryMutation();

  function onDeleteComplete(category: ControlMessageCategory) {
    if (selectedCategoryId === category.id) {
      onSelectCategoryId(ALL);
    }

    setCategoryForm((previous) => {
      const editingCategoryId = parseEditingCategoryId(previous);

      if (editingCategoryId === category.id) {
        return CLOSED;
      }

      return previous;
    });
  }

  async function onDeleteCategory(item: ControlMessageCategory) {
    try {
      await deleteCategoryMutation.mutateAsync(item.id);
      onDeleteComplete(item);
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  }

  function openEditCategory(item: ControlMessageCategory) {
    setCategoryForm(`${EDIT}-${item.id}`);
  }

  return {
    deleteCategory: onDeleteCategory,
    openEditCategory,
  };
}
